import type { IJargonSuggestion } from "../../../lib/api/jargon"

/** A meeting's suggestions, kept together under one header in the queue. */
export interface IJargonMeetingGroup {
  meetingId: string
  /** The meeting's subject, or null when it couldn't be resolved (deleted / out of scope). */
  subject: string | null
  suggestions: IJargonSuggestion[]
}

/**
 * Group a flat, newest-first suggestion list by its source meeting, preserving
 * that order: the meeting whose most-recent suggestion appears first leads the
 * list, and rows stay in their incoming order within each group. Pure — the
 * grouping is derived, never mutated in place, so it's cheap to memoise.
 */
export function groupJargonByMeeting(suggestions: IJargonSuggestion[]): IJargonMeetingGroup[] {
  const groups: IJargonMeetingGroup[] = []
  const byMeeting = new Map<string, IJargonMeetingGroup>()

  for (const s of suggestions) {
    const existing = byMeeting.get(s.meetingId)
    if (existing) {
      existing.suggestions.push(s)
      continue
    }
    const group: IJargonMeetingGroup = { meetingId: s.meetingId, subject: s.meetingSubject, suggestions: [s] }
    byMeeting.set(s.meetingId, group)
    groups.push(group)
  }

  return groups
}

/** A term seen in a single meeting, and first suggested more than two weeks ago. */
const SEEN_ONCE_AGE_MS = 14 * 24 * 60 * 60 * 1000

export interface IJargonSeenOncePartition {
  /** Still worth a look: recent, heard in more than one meeting, or undated. */
  review: IJargonSuggestion[]
  /** Folded to the bottom. Nothing is deleted. */
  seenOnce: IJargonSuggestion[]
}

/**
 * Postgres stores `created_at` as `NOW()::text`, which is not always ISO
 * (`2026-05-31 10:00:00+00`). ISO parses as-is; the space-separated form gets a
 * `T` and a colon in a short offset. Kept here so this module stays free of
 * the date helpers that pull Lingui into unit tests.
 */
function createdAtMs(value: string | null): number | null {
  if (!value) return null
  const withT = value.trim().replace(" ", "T")
  const normalised = withT.replace(/([+-])(\d{2})(\d{2})?$/, (_match, sign: string, hh: string, mm?: string) => {
    return `${sign}${hh}:${mm ?? "00"}`
  })
  const time = Date.parse(normalised)
  return Number.isNaN(time) ? null : time
}

/**
 * Fold terms that were heard in only one meeting and are strictly older than
 * 14 days into `seenOnce`. The meeting count is per spelling (trimmed,
 * case-insensitive), so two rows of the same word in one meeting still fold
 * when both are old. A null or unparseable date stays in `review`. Frequency
 * does not keep a one-meeting term in the main list. Order inside each bucket
 * is the incoming order.
 */
export function partitionSeenOnce(suggestions: IJargonSuggestion[], now: number): IJargonSeenOncePartition {
  const cutoff = now - SEEN_ONCE_AGE_MS
  const meetingsByTerm = new Map<string, Set<string>>()
  for (const suggestion of suggestions) {
    const key = suggestion.term.trim().toLowerCase()
    const meetings = meetingsByTerm.get(key)
    if (meetings) {
      meetings.add(suggestion.meetingId)
      continue
    }
    meetingsByTerm.set(key, new Set([suggestion.meetingId]))
  }

  const review: IJargonSuggestion[] = []
  const seenOnce: IJargonSuggestion[] = []
  for (const suggestion of suggestions) {
    const meetings = meetingsByTerm.get(suggestion.term.trim().toLowerCase())
    const created = createdAtMs(suggestion.createdAt)
    const oneMeeting = meetings?.size === 1
    if (oneMeeting && created !== null && created < cutoff) {
      seenOnce.push(suggestion)
      continue
    }
    review.push(suggestion)
  }
  return { review, seenOnce }
}
